import React, { useEffect, useState } from 'react';
import WcbCallContainer, { MobileErrorType } from './WcbCallContainer';
import WcbCallNowContainer from './WcbCallNowContainer';
import WcbDateTimeContainer from './WcbDateTimeContainer';
import WcbQueueContainer from './WcbQueueContainer';
import WcbButton from './WcbButton';
import PhoneNumber from './PhoneNumber';
import CallHeader from './CallHeader';
import CallHeaderOld from './CallHeaderOld';
import WcbQueueKeyContainer from './WcbQueueKeyContainer';
import WcbNameContainer from './WcbNameContainer';
import WcbNumberContainer from './WcbNumberContainer';
import CallClosedStatus from './CallClosedStatus';
import WcbButtonOld from './WcbButtonOld';
import { WcbQueueType } from '../types/WcbQueueType';
import { WcbSlotQueueType } from '../types/WcbSlotQueueType';
import { ConfigClientType } from '../types/ConfigClientType';
import { ApiType } from '../types/ApiType';
import { VisibilityType } from '../types/VisibilityType';
import CallSchedule from './CallSchedule';
import { WcbCallTimesType } from '../types/WcbCallTimesType';
import { IconCheckMark } from '../common/svgicons/CheckMark';
import { IconLoaderDotsHorizontal } from '../common/svgicons/LoaderDotsHorizontal';
import { IconClose } from '../common/svgicons/Close';
import { getTranslation, isDanish, isNorwegian, isSwedish } from '../../constants/languages';
import { getCallDateFormat, getCallTimeFormat } from './CallMenu.DateFNS';
import { isPhoneNumberValid, sanitizePhoneNumber } from '../../util/phoneNumber';

type CallMenuProps = {
    iimclient: ConfigClientType;
    api: ApiType;
    initState?: number;
    displayName?: string;
    mobile?: string;
    changeDisplayName: Function;
    changeMobile: Function;
    visibility: VisibilityType;
};

const CallbackNotAvailable = {
    lang_no: 'Beklager, men det er ikke mulig å legge igjen en callback nå.',
    lang_se: 'Sorry, but it is not possible to leave a callback now.', // was English in sweden, kept it as is
    lang_fi: 'Pahoittelut, mutta soittopyynnön jättäminen ei ole tällä hetkellä mahdollista.',
    lang_dk: 'Beklager, men det er ikke muligt at forlade en tilbagekald nu.',
    lang_en: 'Sorry, callback is not available at the moment.',
};

const LoadingCallbackTimes = {
    lang_no: 'Vennligst vent et øyeblikk.',
    lang_se: 'Vänligen vänta ett ögonblick.',
    lang_fi: 'Ole hyvä ja odota hetki.',
    lang_dk: null,
    lang_en: 'Please wait a moment.',
};

const CallMenu = ({
    iimclient,
    api,
    initState,
    displayName,
    mobile,
    changeDisplayName,
    changeMobile,
    visibility,
}: CallMenuProps) => {
    const { lang, segment, nocom } = iimclient;

    const callbackNotAvailable = getTranslation(lang, CallbackNotAvailable);
    const loadingCallbackTimes = getTranslation(lang, LoadingCallbackTimes);

    const [selectedQueueId, setSelectedQueueId] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedCallCode, setSelectedCallCode] = useState('');
    const [selectedQueueKey, setSelectedQueueKey] = useState('');
    const [wcbNumber, setWcbNumber] = useState('');
    const [wcbResultText, setWcbResultText] = useState('');
    const [wcbDescription, setWcbDescription] = useState('');
    const [wcbSchedule, setWcbSchedule] = useState('');
    const [wcbCallTimes, setWcbCallTimes] = useState<WcbCallTimesType | undefined>(undefined);
    const [wcbQueues, setWcbQueues] = useState<WcbQueueType[]>([]);
    const [wcbDates, setWcbDates] = useState<string[]>([]);
    const [wcbTimes, setWcbTimes] = useState<string[]>([]);
    const [wcbCallCodes, setWcbCallCodes] = useState([]);
    const [wcbQueuesLoading, setWcbQueuesLoading] = useState(false);
    const [wcbDateTimeVisible, setWcbDateTimeVisible] = useState(false);
    const [wcbCallNowVisible, setWcbCallNowVisible] = useState(false);
    const [wcbCallNow, setWcbCallNow] = useState(false);
    const [wcbButtonInitState, setWcbButtonInitState] = useState(Date.now());
    const [wcbQueueErrorVisible, setWcbQueueErrorVisible] = useState(false);
    const [displayNameErrorVisible, setDisplayNameErrorVisible] = useState(false);
    const [mobileError, setMobileError] = useState<MobileErrorType | undefined>(undefined);
    const [wcbNumberErrorVisible, setWcbNumberErrorVisible] = useState(false);
    const [wcbDateErrorVisible, setWcbDateErrorVisible] = useState(false);
    const [wcbTimeErrorVisible, setWcbTimeErrorVisible] = useState(false);
    const [wcbErrorVisible, setWcbErrorVisible] = useState(false);
    const [wcbOkVisible, setWcbOkVisible] = useState(false);
    const [wcbInitState, setWcbInitState] = useState(Date.now());

    useEffect(() => {
        if (initState || nocom) displayCallbackForSegment();
    }, [initState]);

    useEffect(() => {
        changeQueue(selectedQueueId);
    }, [selectedQueueId]);

    useEffect(() => {
        updateQueues('', '');
        updateWcbDescription();
    }, [wcbCallTimes]);

    useEffect(() => {
        if (!wcbQueues.length) changeCallbackType('');

        setWcbQueuesLoading(false);
    }, [wcbQueues]);

    useEffect(() => {
        if (selectedQueueId) {
            setWcbDateTimeVisible(!wcbCallNow);
        } else {
            setWcbDateTimeVisible(false);
        }
    }, [wcbCallNow]);

    useEffect(() => {
        updateWcbDescription();
    }, [wcbQueuesLoading]);

    useEffect(() => {
        setWcbNumberErrorVisible(false);
        setWcbQueueErrorVisible(false);
        setWcbErrorVisible(false);
        setWcbOkVisible(false);
        setWcbResultText('');
    }, [wcbInitState]);

    const updateSelectedDate = (date: string) => {
        console.log('selected date: ' + date);
        changeDate(date);
    };

    const updateSelectedTime = (time: string) => {
        console.log('selected time: ' + time);
        changeTime(time);
    };

    const updateWcbDescription = () =>
        wcbQueuesLoading && loadingCallbackTimes
            ? setWcbDescription(loadingCallbackTimes)
            : wcbCallTimes &&
              (wcbCallTimes.callnow.length || Object.keys(wcbCallTimes.slots).length)
            ? setWcbDescription(segment ? segment.description : 'wcb-description')
            : setWcbDescription(callbackNotAvailable);

    const updateDisplayName = (displayName: string) => {
        setDisplayNameErrorVisible(!displayName);
        changeDisplayName(displayName);
    };

    const updateMobile = (mobile: string) => {
        setMobileError(undefined);
        changeMobile(mobile);
    };

    const updateQueues = function (newDate: string, newTime: string) {
        if (!wcbCallTimes) return;
        let queues: WcbQueueType[] = [];
        Object.keys(wcbCallTimes.slots).forEach((d) => {
            if (newDate == d || !newDate) {
                Object.keys(wcbCallTimes.slots[d]).forEach((t) => {
                    if (newTime == t || !newTime) {
                        wcbCallTimes.slots[d][t].queues.forEach((cq: WcbSlotQueueType) => {
                            if (
                                queues.filter(
                                    (q: WcbQueueType) => q.cbqueueid == cq.queue.cbqueueid
                                ).length == 0
                            ) {
                                queues.push(cq.queue);
                            }
                        });
                    }
                });
            }
        });

        wcbCallTimes.callnow.forEach((cq: WcbQueueType) => {
            if (queues.filter((q) => q.cbqueueid == cq.cbqueueid).length == 0) {
                queues.push(cq);
            }
        });

        queues.sort(function compare(a, b) {
            if (a.sortorder < b.sortorder) return -1;
            if (a.sortorder > b.sortorder) return 1;
            return 0;
        });

        setWcbQueues(queues);
    };

    const updateDates = (queue: WcbQueueType | undefined) => {
        if (!queue || !wcbCallTimes) {
            setWcbDates([]);
            return [];
        }
        const dates: string[] = [];
        Object.keys(wcbCallTimes.slots).forEach((date) => {
            if (Object.keys(wcbCallTimes.slots[date]).length) {
                var queueHasSlots = false;

                Object.keys(wcbCallTimes.slots[date]).every((t) => {
                    queueHasSlots =
                        wcbCallTimes.slots[date][t].queues.filter(
                            (cq: WcbSlotQueueType) => cq.queue.cbqueueid == queue.cbqueueid
                        ).length > 0;
                    return !queueHasSlots;
                });

                if (queueHasSlots) {
                    dates.push(date);
                }
            }
        });
        setWcbDates(dates);
        return dates;
    };

    const updateTimes = function (queue: WcbQueueType | undefined, date: string) {
        if (!queue || !wcbCallTimes) {
            setWcbTimes([]);
            return;
        }
        const times: string[] = [];
        var selectedDate = date;
        if (selectedDate) {
            Object.keys(wcbCallTimes.slots[selectedDate]).forEach((t) => {
                var queueHasSlot =
                    wcbCallTimes.slots[selectedDate][t].queues.filter(
                        (cq: WcbSlotQueueType) => cq.queue.cbqueueid == queue.cbqueueid
                    ).length > 0;
                if (queueHasSlot) {
                    times.push(t);
                }
            });
        }
        setWcbTimes(times);
    };

    const changeCallbackType = function (callbacktype: string) {
        if (!callbacktype) {
            setWcbDateTimeVisible(false);
            setWcbCallNowVisible(false);
            setWcbCallNow(false);
        } else if (callbacktype == 'now') {
            setWcbDateTimeVisible(false);
            setWcbCallNowVisible(false);
            setWcbCallNow(true);
        } else if (callbacktype == 'datetime_and_now') {
            setWcbDateTimeVisible(true);
            setWcbCallNowVisible(true);
            setWcbCallNow(false);
        } else if (callbacktype == 'datetime') {
            setWcbDateTimeVisible(true);
            setWcbCallNowVisible(false);
            setWcbCallNow(false);
        }

        if (
            callbacktype == 'datetime_and_now' &&
            wcbCallTimes &&
            wcbCallTimes.callnow.filter(
                (q: WcbQueueType) => q.cbqueueid.toString() == selectedQueueId
            ).length == 0
        ) {
            setWcbCallNowVisible(false);
        }
    };

    const changeQueue = function (queueId: string) {
        if (queueId) {
            setWcbQueueErrorVisible(false);
        }
        if (!queueId) {
            setWcbSchedule('');
            updateDates(undefined);
            updateTimes(undefined, '');
            changeCallbackType('');
            return false;
        }

        var queue = wcbQueues.find((q: WcbQueueType) => q.cbqueueid.toString() == queueId);

        if (queue && !nocom) {
            setWcbSchedule(!queue.wcb_schedule ? '' : queue.wcb_schedule);
            iimclient.wcb_ok_message = undefined;
        }

        const callbacktype =
            queue && queue.callbacktype ? queue.callbacktype : segment.callbacktype;

        changeCallbackType(callbacktype);

        if (callbacktype && (callbacktype == 'datetime' || callbacktype == 'datetime_and_now')) {
            const dates = updateDates(queue);
            updateTimes(queue, dates.length ? dates[0] : '');
        } else {
            updateDates(undefined);
            updateTimes(undefined, '');
        }
    };

    const changeDate = function (date: string) {
        if (date) setWcbDateErrorVisible(false);

        var queueId = selectedQueueId;
        if (!queueId) {
            updateDates(undefined);
            updateTimes(undefined, '');
            changeCallbackType('');
            setSelectedDate('');
            return false;
        }

        var queue = wcbQueues.filter((q: WcbQueueType) => q.cbqueueid.toString() == queueId)[0];

        updateTimes(queue, date);
        setSelectedDate(date);
    };

    const changeTime = (time: string) => {
        if (time) setWcbDateErrorVisible(false);

        var queueId = selectedQueueId;
        if (!queueId) {
            updateDates(undefined);
            updateTimes(undefined, '');
            changeCallbackType('');
            setSelectedTime('');
            return false;
        }

        setSelectedTime(time);
    };

    const clickWcbButton = () => {
        if (!selectedQueueId && !nocom) {
            setWcbButtonInitState(Date.now());
            setWcbQueueErrorVisible(true);
            return;
        }

        if (!mobile) {
            setWcbButtonInitState(Date.now());
            setMobileError(MobileErrorType.Empty);
            return;
        }

        const sanitizedPhoneNumber = sanitizePhoneNumber(mobile);

        if (!isPhoneNumberValid(selectedCallCode, sanitizedPhoneNumber)) {
            setWcbButtonInitState(Date.now());
            setMobileError(MobileErrorType.Invalid);
            return;
        }

        (window as any).NordicTracking.eventInternalFormSubmit(
            false,
            (window as any).NordicTracking.PANELINFO_FUNCTION_NAME_PHONE
        );

        var start = new Date().getTime();

        hideWcbErrors();

        const postData = {
            queueid: selectedQueueId,
            date: getCallDateFormat(selectedDate, selectedTime, wcbCallTimes),
            time: getCallTimeFormat(selectedDate, selectedTime, wcbCallTimes),
            callnow: wcbCallNow,
            name: displayName,
            phone_code: selectedCallCode,
            phone: sanitizedPhoneNumber,
        };

        console.log(postData);

        api.orderCallback(segment.cbsegmentid, postData, function (err, res) {
            const stop = new Date().getTime();
            Math.floor(((stop - start) % (1000 * 60)) / 1000) < 500
                ? setTimeout(() => setWcbButtonInitState(Date.now()), 500)
                : setWcbButtonInitState(Date.now());

            if (err) {
                showWcbError(err.message, err.element);
                return;
            }

            changeWcbOkVisible(true, iimclient.wcb_ok_message || res.message);
            setWcbSchedule('');
        });
    };

    function displayCallbackForSegment() {
        if (!segment) return;

        setDisplayNameErrorVisible(false);
        setMobileError(undefined);

        setSelectedQueueId('');
        setWcbCallCodes(segment.countrycallingcodes ? segment.countrycallingcodes.split(',') : []);
        setWcbQueuesLoading(true);

        if (iimclient.augmentIdentitySilent) {
            iimclient.augmentIdentitySilent();
        }

        api.getAvailableWebCallbackTimes(
            segment.cbsegmentid,
            null, // selected queue
            function (err, res) {
                if (err) {
                    setWcbQueuesLoading(false);
                    console.log('' + err);
                    return;
                }
                let callTimes = res;

                // we need to re-define these for the current local time

                var newCallTimes = { callnow: [], slots: {} };

                Object.keys(callTimes.slots).forEach((d) => {
                    Object.keys(callTimes.slots[d]).forEach((t) => {
                        var temp = new Date(d + 'T' + t + ':00.000Z');
                        var local = new Date(
                            temp.getFullYear(),
                            temp.getMonth(),
                            temp.getDate(),
                            temp.getHours(),
                            temp.getMinutes(),
                            temp.getSeconds()
                        );
                        const UTCDate = local.getUTCDate();
                        const localDate = local.getDate();
                        const localMonth = local.getMonth();
                        const localYear = local.getFullYear();
                        const localHours = local.getHours();
                        const localMinutes = local.getMinutes();

                        const date = localDate < 10 ? `0${localDate}` : localDate;

                        const month = localMonth + 1 < 10 ? `0${localMonth + 1}` : localMonth + 1;

                        const year = localYear < 10 ? `0${localYear}` : localYear;

                        const hours = localHours < 10 ? `0${localHours}` : localHours;

                        const minutes = localMinutes < 10 ? `0${localMinutes}` : localMinutes;

                        const dateformat = `${year}-${month}-${date}`;
                        const timeformat = `${hours}:${minutes}`;

                        var isLast =
                            Object.keys(callTimes.slots)[Object.keys(callTimes.slots).length - 1] ==
                            d;
                        if (!newCallTimes.slots[dateformat] && (!isLast || localDate <= UTCDate)) {
                            newCallTimes.slots[dateformat] = {};
                        }

                        if (newCallTimes.slots[dateformat]) {
                            newCallTimes.slots[dateformat][timeformat] = {
                                queues: callTimes.slots[d][t],
                                utc: d + 'T' + t + ':00.000Z',
                            };
                        }

                        newCallTimes.callnow = callTimes.callnow;
                    });
                });

                callTimes = newCallTimes;
                setWcbCallTimes(callTimes);
            }
        );
    }
    iimclient.displayCallbackForSegment = displayCallbackForSegment;

    const hideWaitTimeText =
        segment &&
        (!iimclient.phoneVisualQueueId || (segment.chatqueue && segment.chatqueue.hidewaitingtime));
    const hideCallbackBox =
        iimclient.hideCallback || (segment && !segment.callbacktype && !segment.callbackqueue);
    const hideCallbackButton = iimclient.hideCallbackStartBtn;
    const hidePhoneBox = iimclient.hidePhoneBox;
    const isSwedishSite =
        isSwedish(iimclient) &&
        !segment &&
        iimclient.callbackQueues &&
        iimclient.callbackQueues.length;

    const clickCallback = (event?: React.MouseEvent<HTMLElement>) => {
        if (isDanish(iimclient) && iimclient.callback_button_url) {
            // Replace the callback with link to contact-us page
            if (event) {
                event.preventDefault();
            }
            window.location.href = iimclient.callback_button_url;
            return;
        }

        if (iimclient.toggleCallback) {
            iimclient.toggleCallback(event);
            setWcbInitState(Date.now());
        }
    };

    const changeSelectedQueueKey = (key) => {
        setWcbQueueErrorVisible(!key);
        setSelectedQueueKey(key);
    };

    // change to use translations from CallCallbackStart
    const callbackButtonText = () => {
        return segment && segment.buttontext
            ? segment.buttontext
            : iimclient.texts['call-callback-start'] && !nocom
            ? iimclient.texts['call-callback-start']
            : 'Bli oppringt';
    };

    const meetingBox = () =>
        (isDanish(iimclient) || isNorwegian(iimclient)) &&
        iimclient.texts['meeting-box-html'] && (
            <span dangerouslySetInnerHTML={{ __html: iimclient.texts['meeting-box-html'] }} />
        );

    const changeWcbErrorVisible = (visible: boolean, text?: string) => {
        if (visible) {
            setWcbResultText(text ? text : iimclient.texts['error']);
        }
        setWcbErrorVisible(visible);
    };

    const changeWcbOkVisible = (visible: boolean, text?: string) => {
        if (visible) {
            setWcbResultText(text ? text : iimclient.texts['callbackOK']);
        }
        setWcbOkVisible(visible);
    };

    const showWcbError = (message: string, element: string) => {
        switch (element) {
            case 'wcb-queueid':
                setWcbQueueErrorVisible(true);
                return;
            case 'wcb-date':
                setWcbDateErrorVisible(true);
                return;
            case 'wcb-time':
                setWcbTimeErrorVisible(true);
                return;
            case 'wcb-name':
                setDisplayNameErrorVisible(true);
                return;
            case 'wcb-phone':
                setMobileError(MobileErrorType.Invalid);
                return;
        }

        changeWcbErrorVisible(true, message);
    };

    const hideWcbErrors = () => {
        setWcbQueueErrorVisible(false);
        setWcbDateErrorVisible(false);
        setWcbTimeErrorVisible(false);
        setDisplayNameErrorVisible(false);
        setWcbErrorVisible(false);
        setMobileError(undefined);
    };

    const isCallbackOpenVisible = !hideCallbackBox && visibility.callbackOpenVisible;

    if (visibility.activeMenu === 'wcb' && (!isCallbackOpenVisible || hideCallbackButton)) {
        visibility.activeMenu = 'call';
    }

    const isCallVisible = visibility.activeMenu === 'call';
    const isCallbackVisible = visibility.activeMenu === 'wcb';
    const isPhoneboxVisible = !hidePhoneBox && (isCallVisible || isCallbackVisible);
    const isAfterPhoneVisible =
        !hideWaitTimeText && visibility.callOpenVisible && iimclient.phoneWaitTimeText;

    return (
        isPhoneboxVisible && (
            <div className="openbox phonebox">
                <div className="content">
                    {isCallVisible && (
                        <div className="call">
                            <CallHeader iimclient={iimclient} />
                            <CallClosedStatus iimclient={iimclient} visibility={visibility} />
                            <PhoneNumber iimclient={iimclient} api={api} />
                            {isAfterPhoneVisible && (
                                <p id="waitTimeText" className="whenopen after-phone">
                                    {iimclient.phoneWaitTimeText && (
                                        <>
                                            {/* add translations here for AverageWaitTime */}
                                            {iimclient.texts['avgWaitTime']}
                                            <span className="timevalue">
                                                {iimclient.phoneWaitTimeText}
                                            </span>
                                        </>
                                    )}
                                </p>
                            )}
                            <CallSchedule iimclient={iimclient} />
                            {isCallbackOpenVisible && (
                                <div className="callbackbox whenopen">
                                    {/* add translations here for CallQueueDescription */}
                                    {iimclient.texts['call-queue-description'] && (
                                        <p
                                            id="call-queue-description"
                                            dangerouslySetInnerHTML={{
                                                __html: iimclient.texts['call-queue-description'],
                                            }}
                                        />
                                    )}
                                    {!hideCallbackButton && (
                                        <button
                                            className="if button secondary u-margin-top--16 callbackStartBtn"
                                            onClick={clickCallback}
                                        >
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: callbackButtonText(),
                                                }}
                                            />
                                        </button>
                                    )}
                                </div>
                            )}
                            {meetingBox()}
                        </div>
                    )}

                    {isCallbackVisible && segment && (
                        <div className="callback new">
                            <span className="heading" id="wcb-header">
                                {/* add translations from Call */}
                                {segment ? segment.header : 'Ring meg'}
                            </span>
                            {!wcbOkVisible && <p id="wcb-description">{wcbDescription}</p>}
                            {wcbSchedule && (
                                <p id="wcb-schedule">
                                    <span dangerouslySetInnerHTML={{ __html: wcbSchedule }} />
                                </p>
                            )}
                            {wcbQueuesLoading && (
                                <div id="wcb-load-spinner">
                                    <IconLoaderDotsHorizontal
                                        width="64px"
                                        height="64px"
                                        color="#331e11"
                                    />
                                    {/* check values */}
                                </div>
                            )}
                            {!wcbOkVisible && (
                                <form id="wcb-form">
                                    <WcbQueueContainer
                                        iimclient={iimclient}
                                        wcbQueues={wcbQueues}
                                        wcbQueueErrorVisible={wcbQueueErrorVisible}
                                        queueSelectDisabled={wcbQueuesLoading}
                                        selectedQueueId={selectedQueueId}
                                        changeSelectedQueueId={setSelectedQueueId}
                                    />
                                    <WcbDateTimeContainer
                                        iimclient={iimclient}
                                        showContainer={wcbDateTimeVisible}
                                        wcbDates={wcbDates}
                                        wcbTimes={wcbTimes}
                                        wcbDateErrorVisible={wcbDateErrorVisible}
                                        wcbTimeErrorVisible={wcbTimeErrorVisible}
                                        changeSelectedDate={updateSelectedDate}
                                        changeSelectedTime={updateSelectedTime}
                                    />
                                    <WcbCallNowContainer
                                        iimclient={iimclient}
                                        showContainer={wcbCallNowVisible}
                                        wcbCallNow={wcbCallNow}
                                        changeWcbCallNow={setWcbCallNow}
                                    />
                                    {segment && segment.namelabel && (
                                        <div
                                            className="input-block wcb-name-container"
                                            id="wcb-name-container"
                                        >
                                            <label htmlFor="wcb-name" id="wcb-namelabel">
                                                {segment && segment.namelabel
                                                    ? segment.namelabel
                                                    : 'name'}
                                            </label>
                                            <input
                                                name="wcb-name"
                                                type="text"
                                                id="wcb-name"
                                                className="if input-field"
                                                aria-labelledby="wcb-name"
                                                value={displayName}
                                                onChange={(e) => updateDisplayName(e.target.value)}
                                            />
                                            {displayNameErrorVisible && (
                                                <span id="wcb-name-error" className="errorMessage">
                                                    {segment && segment.namerequired
                                                        ? segment.namerequired
                                                        : ''}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <WcbCallContainer
                                        iimclient={iimclient}
                                        wcbCallCodes={wcbCallCodes}
                                        mobile={mobile}
                                        mobileError={mobileError}
                                        changeSelectedCallCode={setSelectedCallCode}
                                        changeMobile={updateMobile}
                                    />
                                    <WcbButton
                                        iimclient={iimclient}
                                        clickButton={clickWcbButton}
                                        initState={wcbButtonInitState}
                                    />
                                    {wcbErrorVisible && (
                                        <p className="resp-msg errorMessage" id="wcb-error">
                                            {wcbResultText}
                                        </p>
                                    )}
                                </form>
                            )}
                            {wcbOkVisible && (
                                <div className="callback-result" aria-hidden="true">
                                    <IconCheckMark width="64px" height="64px" />
                                    {/* check the values */}
                                    <p
                                        className="callback-result-text"
                                        dangerouslySetInnerHTML={{ __html: wcbResultText }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {isCallbackVisible && !segment && (
                        <div className="callback old">
                            {/* add translations from CallCallbackHeader */}
                            {nocom ? (
                                <span
                                    className="heading"
                                    id="call-header"
                                    dangerouslySetInnerHTML={{
                                        __html: iimclient.texts['call-callback-header']
                                            ? iimclient.texts['call-callback-header']
                                            : 'Bli oppringt',
                                    }}
                                />
                            ) : (
                                <CallHeaderOld iimclient={iimclient} />
                            )}
                            {!(wcbOkVisible || wcbErrorVisible) && (
                                <div className="callback-content">
                                    {visibility.callbackOpenVisible && (
                                        <div className="callbackbox whenopen">
                                            {nocom && (
                                                <p id="call-callback-description">
                                                    Vi ringer deg opp ved spørsmål om alt som har
                                                    med forsikringene dine å gjøre, unntatt skade.
                                                    <br />
                                                    Ved skade, ring&nbsp;
                                                    <a
                                                        style={{ margin: '0px' }}
                                                        href="tel:+4721492400"
                                                    >
                                                        21 49 24 00
                                                    </a>
                                                    <br />
                                                    <a href="https://www.if.no/bedrift/meld-skade">
                                                        eller besøk skadesiden vår
                                                    </a>
                                                </p>
                                            )}
                                            {/* add translations from CallCallbackDescription */}
                                            {!(isDanish(iimclient) || isSwedishSite) && (
                                                <p
                                                    id="call-callback-description"
                                                    dangerouslySetInnerHTML={{
                                                        __html: iimclient.texts[
                                                            'call-callback-description'
                                                        ]
                                                            ? iimclient.texts[
                                                                  'call-callback-description'
                                                              ]
                                                            : 'Hvis det er en kø på tidspunktet for samtalen, kan du legge igjen en tilbakeringingsforespørsel',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                    {!nocom && (
                                        <WcbNameContainer
                                            iimclient={iimclient}
                                            displayName={displayName}
                                            changeDisplayName={changeDisplayName}
                                        />
                                    )}
                                    <WcbNumberContainer
                                        iimclient={iimclient}
                                        wcbNumber={wcbNumber}
                                        wcbNumberErrorVisible={wcbNumberErrorVisible}
                                        changeCallbackNumber={setWcbNumber}
                                    />
                                    {!nocom && (
                                        <WcbQueueKeyContainer
                                            iimclient={iimclient}
                                            wcbQueueErrorVisible={wcbQueueErrorVisible}
                                            changeSelectedQueue={changeSelectedQueueKey}
                                        />
                                    )}
                                    {/* add translations from CallCallbackDescription */}
                                    {isSwedishSite && (
                                        <p
                                            id="call-callback-description"
                                            dangerouslySetInnerHTML={{
                                                __html: iimclient.texts['call-callback-description']
                                                    ? iimclient.texts['call-callback-description']
                                                    : '',
                                            }}
                                        />
                                    )}
                                    <WcbButtonOld
                                        iimclient={iimclient}
                                        api={api}
                                        callbackNumber={wcbNumber}
                                        selectedQueueKey={selectedQueueKey}
                                        displayName={displayName}
                                        changeWcbNumberErrorVisible={setWcbNumberErrorVisible}
                                        changeWcbQueueErrorVisible={setWcbQueueErrorVisible}
                                        changeWcbErrorVisible={changeWcbErrorVisible}
                                        changeWcbOkVisible={changeWcbOkVisible}
                                    />
                                </div>
                            )}
                            {(wcbOkVisible || wcbErrorVisible) && (
                                <div
                                    className={`callback-result ${
                                        wcbOkVisible ? 'result-ok' : ''
                                    } ${wcbErrorVisible ? 'result-error' : ''}`}
                                    aria-hidden="true"
                                >
                                    <IconCheckMark width="26px" height="26px" />
                                    {/* check these values */}
                                    {wcbErrorVisible && <IconClose width="26px" height="26px" />}
                                    {/* check these values */}
                                    <p
                                        className="callback-result-text"
                                        dangerouslySetInnerHTML={{ __html: wcbResultText }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    );
};

export default CallMenu;
